import { useState } from 'react';
import toast from 'react-hot-toast';

import { cn, io } from 'lib/helpers/utility';

import Icon from 'components/shared/Icon/Icon';

import Footer from './components/Footer';
import {
  COLORS,
  SIZES,
  VARIANTS,
  getBackgroundClass,
  getBorderClass,
  getIconColor,
  getIconShade,
  getShade,
  getSizeClasses,
  getTextClass
} from './lib/helpers';

/**
 * @param {import('./lib/propTypes').AlertProps} props
 */

export const Alert = ({
  icon = 'info-filled',
  color = 'neutral',
  variant,
  size,
  title,
  message,
  footerActions,
  width = 400,
  handleClose,
  className: wrapperClassName = ''
}) => {
  // Default size: 'large' if title is long (>22 chars) or if both title and message are provided and different and has footer actions
  const mainText = title || message || '';
  const hasFooter = footerActions && io(footerActions);
  size =
    title && message
      ? 'large'
      : (mainText.length > 22 || (title && message && title !== message)) && hasFooter
        ? 'large'
        : 'small';

  if (!COLORS.includes(color)) color = 'primary';
  if (!variant || !VARIANTS.includes(variant)) {
    variant = color === 'success' && size === 'large' ? 'lighter' : 'outlined';
  }
  if (!SIZES.includes(size)) size = 'small';

  const [footerLoadingStates, setFooterLoadingStates] = useState({});
  const sizeConfig = getSizeClasses(size);
  const isTitleAndMessageSame = title && message && title === message;
  const isTitleLink = typeof title === 'string' && title.includes('http');

  const handleFooterActionClick = async (
    onClick = () => null,
    actionType = 'primary',
    closeOnClick = false
  ) => {
    try {
      setFooterLoadingStates((prev) => ({
        ...prev,
        [actionType]: true
      }));
      await onClick();
      setFooterLoadingStates((prev) => ({
        ...prev,
        [actionType]: false
      }));
      if (closeOnClick) {
        handleClose?.();
      }
    } catch (error) {
      console.error('Footer action error:', error);
      setFooterLoadingStates((prev) => ({
        ...prev,
        [actionType]: false
      }));
    }
  };

  return (
    <div
      className={cn(
        'flex h-fit gap-2 overflow-hidden rounded-md',
        size === 'large' ? 'items-start' : 'items-center',
        width === 'full' ? 'w-full' : `w-[${width}px]`,
        title?.length > 50 && '!h-auto items-start',
        getBackgroundClass(color, variant),
        getBorderClass(color, variant),
        getShade(variant),
        sizeConfig.padding,
        sizeConfig.height,
        wrapperClassName
      )}>
      {icon && (
        <Icon
          icon={icon}
          color={getIconColor(color, variant)}
          shade={getIconShade(color, variant)}
          size={sizeConfig.iconSize}
        />
      )}
      <div
        className={cn(
          'flex grow',
          size === 'large' ? 'flex-col' : 'flex-row justify-between gap-4'
        )}>
        {(title || message) && (
          <span
            className={cn(
              'flex items-center text-[12px] first-letter:uppercase',
              sizeConfig.lineHeight,
              sizeConfig.textFontSize,
              sizeConfig.fontWeight,
              getTextClass(color, variant, true)
            )}
            style={{
              wordBreak: isTitleLink ? 'break-all' : 'break-word'
            }}>
            {title || message}
          </span>
        )}
        {message && size === 'large' && !isTitleAndMessageSame && (
          <p
            className={cn(
              'mt-2 flex items-center text-[14px] font-400 first-letter:uppercase',
              sizeConfig.lineHeight,
              getTextClass(color, variant, false)
            )}>
            {typeof message === 'string' && message?.includes?.('<') ? (
              <div dangerouslySetInnerHTML={{ __html: message }} />
            ) : (
              message
            )}
          </p>
        )}
        <Footer
          footerActions={footerActions}
          size={size}
          color={color}
          variant={variant}
          footerLoadingStates={footerLoadingStates}
          handleFooterActionClick={handleFooterActionClick}
          message={message}
          title={title}
        />
      </div>
      {handleClose && (
        <div className="cursor-pointer hover:opacity-80" onClick={handleClose}>
          <Icon
            icon="close-linear"
            size={16}
            color={variant === 'filled' ? 'white' : 'neutral'}
            shade={700}
          />
        </div>
      )}
    </div>
  );
};

/**
 * @param {import('./lib/propTypes').ShowAlertProps} props
 */

export const showAlert = ({
  icon,
  color,
  variant,
  size,
  title,
  message,
  footerActions,
  position = 'top-right',
  duration = title && message ? 5000 : 3000
}) => {
  toast.custom(
    (t) => (
      <Alert
        icon={icon}
        color={color}
        variant={variant}
        size={size}
        title={title || message}
        message={message}
        footerActions={footerActions}
        handleClose={() => toast.remove(t?.id)}
      />
    ),
    { position, duration }
  );
};
